import axiosInstance, { jsonHeaders } from '@/api/axiosInstance';
import { environment } from '@/config/environment';

export interface ColumnNameValue {
  columnName: string;
  value: string;
}

export interface InventoryRunSet {
  runSetId?: string | number;
  id?: string | number;
  status: string;
  totalPairs?: number;
  errorMessage?: string | null;
}

export async function recalculateInventory(source: string, filters: ColumnNameValue[], signal?: AbortSignal) {
  const { data } = await axiosInstance.post<InventoryRunSet>(
    '/inventoryReplenisher/recalculate', { Source: source, Filters: filters },
    { headers: jsonHeaders(), signal, timeout: environment.inventoryTimeout },
  );
  return data;
}

export async function checkInventoryRecalculation(runSetId: string | number, signal?: AbortSignal) {
  const { data } = await axiosInstance.post<InventoryRunSet>(
    '/inventoryReplenisher/checkstatus', { RunSetId: runSetId },
    { headers: jsonHeaders(), signal, timeout: environment.inventoryTimeout },
  );
  return data;
}

export interface InventoryProductAvailabilityRequest {
  unitOfMeasure?: string | null;
  showExceptions?: boolean;
  railcarOnly?: boolean;
  filters?: ColumnNameValue[] | null;
  sorting?: ColumnNameValue[] | null;
  pageNumber?: number | null;
  linesPerPage?: number | null;
  permission?: string | null;
}

export interface InventoryProductAvailabilityResponse {
  count: number;
  data: Record<string, unknown>[];
}

export interface UpdateProductAvailabilityRequest {
  orderNumber: string;
  itemNumber: string;
  itemStatus: string;
  baseMaterial?: string;
  batchId?: string;
  railCarId?: string;
  location?: string;
  plantCode?: string;
  notes?: string;
  unassignBatch?: boolean;
  permission?: string;
}

export interface InventoryRailcarsRequest {
  source?: string | null;
  userId?: string | null;
  productType?: string | null;
  searchFilters?: string | null;
  permission?: boolean;
  linesPerPage?: number | null;
  pageNumber?: number | null;
  orderNumber?: string | null;
  itemNumber?: string | null;
  unitOfMeasure?: string | null;
  supplyPoint?: string | null;
  materialCode?: string | null;
  filters?: ColumnNameValue[] | null;
  sorting?: ColumnNameValue[] | null;
}

export interface RailCar {
  source?: string;
  railCarId?: string;
  batchId?: string;
  unrestricted?: number | null;
  grossWeight?: number | null;
  maxGrossWeight?: number | null;
  unitOfMeasure?: string;
  railCarStatus?: string;
  railCarStatusPriority?: number;
  reservedBy?: string;
  reservedStatus?: string;
  orderNumber?: string;
  itemNumber?: string;
  plantCode?: string;
  materialCode?: string;
  materialDescription?: string;
  loadDate?: string | null;
  planningUnit?: string;
  productSubGroup?: string;
  productFamily?: string;
}

export interface ServiceResponseOfRailCar {
  count?: number;
  data?: RailCar[];
}

export async function getProductAvailability(request: InventoryProductAvailabilityRequest, signal?: AbortSignal) {
  const { data } = await axiosInstance.post<InventoryProductAvailabilityResponse>(
    '/inventoryProductAvailability', request, { headers: jsonHeaders(), signal, timeout: environment.inventoryTimeout },
  );
  return data;
}

export async function getInventoryRailcars(request: InventoryRailcarsRequest, signal?: AbortSignal) {
  const { data } = await axiosInstance.post<ServiceResponseOfRailCar>(
    '/inventoryRailcars', request, { headers: jsonHeaders(), signal, timeout: environment.inventoryTimeout },
  );
  return data;
}

export async function updateInventoryItemStatus(request: UpdateProductAvailabilityRequest) {
  const { data } = await axiosInstance.patch<InventoryProductAvailabilityResponse['data']>(
    '/inventoryProductAvailability', request, { headers: jsonHeaders(), timeout: environment.inventoryTimeout },
  );
  return data;
}

export async function addInventoryStatusNote(orderNumber: string, text: string, signal?: AbortSignal) {
  await axiosInstance.patch('/note', { orderNumber, text }, { headers: jsonHeaders(), signal });
}